import * as THREE from 'three';
import { applyInteriorDark } from '../engine/interior';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { NODES, TERRAIN, ASSETS } from '../config';
import { terrainHeight } from '../engine/terrain';
import { biomeAt, Biome } from '../engine/biome';
import { applyCloudShadow } from '../engine/clouds';
import { creasedNormals } from '../engine/creased';
import { buildAssetModel, type AssetId } from './assets';
import { Inventory, type ItemId, type Stack } from './inventory';
import { type Box, BoxPool, segmentBoxT } from '../engine/collision';
import { preloadGLB, cloneGLBIfReady, onGLBReady } from '../engine/gltf';
import { buildableScale } from '../engine/resizers';

// A stable per-crate seed from its WORLD POSITION (quantized) so every client rolls the SAME loot for the
// same crate (asset layout is deterministic -> identical positions everywhere). mulberry32 is defined below.
export function assetSeed(x: number, z: number): number {
  const sx = Math.round(x * 4) | 0, sz = Math.round(z * 4) | 0;
  return (0x9e3779b9 ^ Math.imul(sx, 2654435761) ^ Math.imul(sz, 40503)) >>> 0;
}

// Per-spot respawn delay: the base ASSETS.respawnSec plus a DETERMINISTIC 0..1x extra keyed off the
// position (same on every client + the server), so the salvage field refills unevenly -- you travel to
// find whichever clusters happen to be back, instead of farming one spot on a fixed timer (owner ask).
export function assetRespawnSec(x: number, z: number): number {
  return ASSETS.respawnSec * (1 + (assetSeed(x, z) % 1000) / 1000);
}

// Roll an asset's loot table (config) into concrete drops. Used by the authority for SMASHED props (scattered
// on the ground) and by the AssetField for OPENED crates (filled into a chest-style container inventory).
// Pass `seed` (a crate's position seed) for a DETERMINISTIC roll so all clients see the same crate contents;
// omit it (admin loot-tally only) for a fresh random roll each call.
export function rollAssetLoot(id: AssetId, seed?: number): Array<{ id: ItemId; amount: number }> {
  const rng = seed === undefined ? Math.random : mulberry32(seed);
  const out: Array<{ id: ItemId; amount: number }> = [];
  for (const [item, min, max, chance] of ASSETS.defs[id].loot) {
    if (chance != null && rng() > chance) continue;
    const amount = min + Math.floor(rng() * (max - min + 1));
    if (amount > 0) out.push({ id: item as ItemId, amount });
  }
  return out;
}

// SALVAGE ASSETS scattered across the island: barrels/crates/junk you find and salvage for materials (the
// roaming loot loop). Deterministic jittered-grid scatter gated by biome (island-wide), instanced + distance-
// streamed like forage/rocks so only nearby props draw. Two salvage verbs (per ASSETS.defs[id].method):
// 'destroy' = smash with a gather tool over `hits` swings, 'open' = press E once. Salvaged props vanish and
// respawn on a wall-clock timer. Each asset's multi-material Group is baked once into a single vertex-coloured
// merged geometry so a whole archetype draws as ONE InstancedMesh. Loot is rolled by the authority (localBackend).

const A_CELL = 48, A_OFF = 4096, A_STRIDE = 8192;
const UP = new THREE.Vector3(0, 1, 0);
const ASSET_SEED = 20260629;

// Composite SITES: some salvage spots cluster a few props together (e.g. barrels around a crate) so you smash the
// barrels and open the crate at one find. Members sit within SITE_R of the centre (well under A_CELL so grid/stream/
// nearest are unaffected) at the centre's ground height, so the cluster reads as one pile rather than a splayed mess.
const SITE_R = 2.3;
const SITE_MULTI_CHANCE = 0.3;                  // fraction of non-junk sites that get extra members
const SITE_PROPS: AssetId[] = ['barrelBlue', 'barrelRed', 'barrelGrey', 'barrelDark', 'crateLarge', 'crateMilitary', 'toolbox'];

// Per-archetype UNSCALED collider half-extents + centre-height (ay is the base; cy lifts the box to the body
// centre). ascale[i] already folds in ASSETS.defs[id].scale, so multiply ONLY by ascale -- never by defs.scale.
// Used for BOTH player/bullet collision (walk-through fix) and precise mouse-pick targeting (one slot per box).
interface AExt { hx: number; hy: number; hz: number; cy: number; }
const AE_BARREL: AExt = { hx: 0.34, hy: 0.55, hz: 0.34, cy: 0.55 }; // ~1.1m tall drum (makeBarrel T=1.2)
const AE_JUNK: AExt = { hx: 0.85, hy: 0.55, hz: 0.85, cy: 0.55 };
const ASSET_EXTENT: Record<string, AExt> = {
  barrelBlue: AE_BARREL, barrelRed: AE_BARREL, barrelGrey: AE_BARREL, barrelDark: AE_BARREL,
  crateLarge: { hx: 0.58, hy: 0.55, hz: 0.58, cy: 0.55 },
  crateMilitary: { hx: 0.55, hy: 0.28, hz: 0.32, cy: 0.24 },
  toolbox: { hx: 0.28, hy: 0.16, hz: 0.16, cy: 0.15 },
  junk1: AE_JUNK, junk2: AE_JUNK, junk3: AE_JUNK, junk4: AE_JUNK, junk5: AE_JUNK, junk6: AE_JUNK,
};
const _P0 = new THREE.Vector3(), _P1 = new THREE.Vector3();
const _BOX: Box = { cx: 0, cy: 0, cz: 0, hx: 0, hy: 0, hz: 0, yaw: 0 };
// Interaction feedback: a quick hit JOLT (wobble decaying over JOLT_DUR) + a sticky OPEN lean while a crate's UI is
// up. Both are whole-instance tilts about a fixed horizontal axis through the base (the geometry has no separate lid).
const _TILT_AXIS = new THREE.Vector3(1, 0, 0);
const _TILT_Q = new THREE.Quaternion();
const JOLT_DUR = 0.22;   // seconds a hit-wobble lasts
const OPEN_TILT = 0.20;  // radians a crate leans back while being looted

// Which asset ids are salvageable in the world (recycler + locked crate are deferred -> excluded).
const SALVAGE_IDS: AssetId[] = [
  'barrelBlue', 'barrelRed', 'barrelGrey', 'barrelDark',
  'crateLarge', 'crateMilitary', 'toolbox',
  'junk1', 'junk2', 'junk3', 'junk4', 'junk5', 'junk6',
];

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

// One salvage prop that differs from the pristine deterministic layout, for the world save. Keyed by
// POSITION (decimetre-rounded, same reasoning as forage: indices shift under seedNear/config tweaks,
// positions re-derive bit-identically or harmlessly miss). Exactly one of the payloads applies:
// `rem` = salvaged, seconds left until respawn (boot clock restarts, so remaining not absolute);
// `hits` = still standing but partially smashed; `cont` = an opened crate's remaining loot (so a
// reload cannot re-roll a fresh table into a half-looted crate).
export interface SavedSalvage { k: string; rem?: number; hits?: number; cont?: Array<Stack | null>; }
function aKey(x: number, z: number): string { return `${Math.round(x * 10)},${Math.round(z * 10)}`; }

// Bake an asset's multi-material Group into ONE merged geometry with the part colours baked into a vertex-colour
// attribute, so the whole archetype can render as a single InstancedMesh (one draw call for all of that kind).
function bakeAssetGeo(id: AssetId): THREE.BufferGeometry {
  const grp = buildAssetModel(id);
  grp.updateMatrixWorld(true);
  const parts: THREE.BufferGeometry[] = [];
  grp.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    const m = o;
    const g = m.geometry.clone();
    g.applyMatrix4(m.matrixWorld);
    const col = (m.material as THREE.MeshStandardMaterial).color;
    const n = g.getAttribute('position').count;
    const ca = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) { ca[i * 3] = col.r; ca[i * 3 + 1] = col.g; ca[i * 3 + 2] = col.b; }
    g.setAttribute('color', new THREE.BufferAttribute(ca, 3));
    parts.push(g);
  });
  const merged = mergeGeometries(parts, false)!;
  for (const p of parts) p.dispose();
  return merged;
}

// --- GLB barrel replacements (owner 2026-07-09) --------------------------------------------------------
// All four standalone barrel colours have real hand-modelled GLBs. Every archetype here still renders as
// ONE shared vertex-coloured InstancedMesh (island-wide scatter needs that to stay cheap), so a textured
// GLB can't just carry its own material in -- its diffuse texture gets BAKED into per-vertex colour
// instead (sampled at each vertex's own UV), keeping far more real paint/rust detail than a single flat
// average colour while adding zero new materials/draw calls. Junk piles keep their own embedded procedural
// barrels regardless (see assets.ts's comment) -- this swap is only the standalone salvage-field archetypes.
const BARREL_GLB: Partial<Record<AssetId, string>> = { barrelBlue: 'barrel_blue.glb', barrelRed: 'barrel_red.glb', barrelDark: 'barrel_black.glb', barrelGrey: 'barrel_silver.glb' };
for (const f of Object.values(BARREL_GLB)) preloadGLB('/models/' + f);

// The owner's meshopt-compressed exports store position as a NORMALIZED Int16 packed into a shared
// INTERLEAVED buffer (small file size, GPU-friendly). Reading through getX/getY/getZ always decodes
// correctly regardless of that packing -- but applyMatrix4's per-vertex write-back does not: it silently
// clamps once a transform pushes a value outside the format's fixed -1..1 range, which is exactly what
// recentring/grounding a raw model needs to do (verified empirically: a plain +5 translation only moved
// vertices by +1 on this attribute type). Copying into a fresh, plain, non-interleaved Float32BufferAttribute
// FIRST sidesteps the range ceiling entirely, since reads are unaffected and the new buffer has no cap.
function toPlainFloatAttribute(attr: THREE.BufferAttribute): THREE.BufferAttribute {
  const arr = new Float32Array(attr.count * attr.itemSize);
  for (let i = 0; i < attr.count; i++) {
    arr[i * attr.itemSize] = attr.getX(i);
    if (attr.itemSize > 1) arr[i * attr.itemSize + 1] = attr.getY(i);
    if (attr.itemSize > 2) arr[i * attr.itemSize + 2] = attr.getZ(i);
  }
  return new THREE.BufferAttribute(arr, attr.itemSize, false);
}

// Bake a loaded GLB scene into ONE merged BufferGeometry that KEEPS its real UVs (position + uv only), plus
// the GLB's own textured material to render it with -- fit the SAME way every other owner buildable is
// (recentre horizontally, rest the bottom on y=0, scale from buildable_resizers.json). We render the real
// 4K texture rather than baking the diffuse into per-vertex COLOUR (the old approach): baking smeared every
// label/rust detail across the sparse cylinder rings -- invisible on a 700k-vert model, a rainbow-streak
// mess on an 8k one (the artefact the owner hit). Costs only one extra draw call per barrel colour, since
// every archetype is already its own InstancedMesh. creasedNormals rebuilds normals downstream, so we drop
// the (quantized) normal attribute here and keep merges attribute-consistent for multi-part models.
function bakeGLBAssetGeo(scene: THREE.Object3D, file: string, fallbackScale: number): { geometry: THREE.BufferGeometry; material: THREE.MeshStandardMaterial } {
  const box = new THREE.Box3().setFromObject(scene);
  const center = new THREE.Vector3(); box.getCenter(center);
  const scale = buildableScale(file, fallbackScale);
  const fit = new THREE.Matrix4().makeScale(scale, scale, scale)
    .multiply(new THREE.Matrix4().makeTranslation(-center.x, -box.min.y, -center.z)); // translate (recentre+ground) THEN scale
  scene.updateMatrixWorld(true);
  const parts: THREE.BufferGeometry[] = [];
  let srcMat: THREE.MeshStandardMaterial | null = null;
  scene.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    const m = Array.isArray(o.material) ? o.material[0] : o.material;
    if (!srcMat) srcMat = m as THREE.MeshStandardMaterial; // barrels are single-material bodies
    const g = o.geometry.clone();
    g.setAttribute('position', toPlainFloatAttribute(g.getAttribute('position'))); // de-quantize/de-interleave before any transform
    for (const name of Object.keys(g.attributes)) if (name !== 'position' && name !== 'uv') g.deleteAttribute(name); // keep position+uv; creasedNormals rebuilds normals, and merge needs matching attrs
    g.applyMatrix4(o.matrixWorld).applyMatrix4(fit);
    parts.push(g);
  });
  const geometry = mergeGeometries(parts, false)!;
  for (const p of parts) p.dispose();
  return { geometry, material: srcMat! };
}
// Build the InstancedMesh render material for a GLB-textured archetype: a fresh COPY of the GLB's own
// material (Object3D.clone(true) shares material refs, so we must never mutate the cached template's
// material) carrying its full texture set, plus the same world dimming everything else in the field gets.
function texturedAssetMaterial(src: THREE.MeshStandardMaterial): THREE.MeshStandardMaterial {
  const m = src.clone();
  m.vertexColors = false;
  applyCloudShadow(m, 'assets');
  applyInteriorDark(m, true); // dim inside a sealed base (instanced world-XZ), same as the shared flat mat
  m.needsUpdate = true;
  return m;
}

export class AssetField {
  readonly group = new THREE.Group();
  private readonly meshes: Record<string, THREE.InstancedMesh> = {};
  private readonly meshList: THREE.InstancedMesh[] = []; // same meshes, array form for one-shot raycasts
  private readonly visible: Record<string, number[]> = {};
  private readonly grid = new Map<number, number[]>();
  private readonly MAX = ASSETS.cap;
  private readonly ax = new Float32Array(this.MAX);
  private readonly az = new Float32Array(this.MAX);
  private readonly ay = new Float32Array(this.MAX);
  private readonly ayaw = new Float32Array(this.MAX);
  private readonly ascale = new Float32Array(this.MAX);
  private readonly aid: AssetId[] = [];          // which asset archetype each slot is
  private readonly alive: boolean[] = [];
  private readonly regrowAt: number[] = [];       // seconds; when a salvaged slot respawns
  private readonly remaining: number[] = [];      // destroy hits left (open assets start at 1)
  private readonly container: (Inventory | null)[] = []; // OPEN crates: their rolled loot (chest-style), null until opened
  private readonly anim = new Map<number, { kind: 'jolt' | 'open'; t0: number }>(); // transient per-slot feedback (hit jolt / open lean)
  private count = 0;
  private streamCellX = NaN; private streamCellZ = NaN;
  private nowSec = 0;
  private rng = mulberry32(ASSET_SEED);
  private readonly tmp = new THREE.Matrix4();
  private readonly tmpPos = new THREE.Vector3();
  private readonly tmpQuat = new THREE.Quaternion();
  private readonly tmpScale = new THREE.Vector3(1, 1, 1);
  private totalWeight = 1;
  private disabled = false;

  constructor() {
    if (typeof location !== 'undefined' && location.hash.includes('noassets')) { this.disabled = true; return; } // kill switch for debugging
    // The SHARED flat material for procedural (non-GLB) props: vertex colours carry each prop's look, cloud
    // shadows fall on it like rocks/trees. Smooth shading + CREASED archetype normals below: drum walls read
    // round, rims/edges stay hard. GLB-textured barrels each get their OWN textured material instead (built
    // per-archetype below) so their real 4K paint/rust shows sharp, not smeared into vertex colours.
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.62, metalness: 0.32 });
    applyCloudShadow(mat, 'assets');
    applyInteriorDark(mat, true); // salvage props dim inside a sealed base (instanced world-XZ)
    for (const id of SALVAGE_IDS) {
      const glbFile = BARREL_GLB[id];
      const glbUrl = glbFile ? '/models/' + glbFile : null;
      const readyScene = glbUrl ? cloneGLBIfReady(glbUrl) : null;
      // GLB already warm (rare on the very first boot, common on a #showcase reload or regenerate()): bake
      // straight from it (real texture + material). Otherwise the procedural shape renders instantly on the
      // shared flat mat, and the real textured GLB swaps in-place the moment it resolves (below) -- an island
      // never sits with a placeholder for long.
      let geo: THREE.BufferGeometry, material: THREE.MeshStandardMaterial;
      if (readyScene) {
        const baked = bakeGLBAssetGeo(readyScene, glbFile!, 1);
        geo = creasedNormals(baked.geometry, 34); material = texturedAssetMaterial(baked.material);
      } else {
        geo = creasedNormals(bakeAssetGeo(id), 34); material = mat;
      }
      const m = new THREE.InstancedMesh(geo, material, Math.min(this.MAX, 128)); // per-kind visible cap (clusters raise density)
      m.castShadow = true; m.receiveShadow = true; m.count = 0; m.frustumCulled = false;
      m.userData.assetId = id; // so a raycast hit resolves back to the archetype
      this.meshes[id] = m; this.meshList.push(m); this.visible[id] = []; this.group.add(m);
      if (glbUrl && !readyScene) {
        onGLBReady(glbUrl, (clone) => {
          const baked = bakeGLBAssetGeo(clone, glbFile!, 1);
          m.geometry.dispose(); // the procedural placeholder buffer
          m.geometry = creasedNormals(baked.geometry, 34); // every existing instance's transform (instanceMatrix) is untouched
          m.material = texturedAssetMaterial(baked.material); // swap the shared flat mat -> this barrel's real texture (shared mat stays alive for other archetypes)
        });
      }
    }
    this.totalWeight = SALVAGE_IDS.reduce((s, id) => s + ASSETS.defs[id].weight, 0);
    this.fill();
  }

  regenerate(): void { if (this.disabled) return; this.fill(); }

  // Seat a prop PROPERLY, never assume: sample the terrain under the whole footprint; too tilted -> no
  // placement at all (a barrel can't stand there); otherwise sit on the HIGHEST contact point with a hair
  // of settle, so nothing clips into the hill and nothing floats off the downhill edge.
  private place(x: number, z: number, id: AssetId): boolean {
    if (this.count >= this.MAX) return false;
    const fr = 0.42 * ASSETS.defs[id].scale;
    let lo = Infinity, hi = -Infinity;
    for (const [ox, oz] of [[0, 0], [fr, 0], [-fr, 0], [0, fr], [0, -fr]] as const) {
      const h = terrainHeight(x + ox, z + oz);
      if (h < lo) lo = h;
      if (h > hi) hi = h;
    }
    if (hi - lo > 0.5 * ASSETS.defs[id].scale) return false; // too steep for a prop to stand
    const y = hi - 0.04; // rest on the high contact, settled a hair into the soil
    const idx = this.count++;
    this.ax[idx] = x; this.az[idx] = z; this.ay[idx] = y; this.ayaw[idx] = this.rng() * Math.PI * 2;
    this.ascale[idx] = ASSETS.defs[id].scale * (0.92 + this.rng() * 0.16);
    this.aid[idx] = id; this.alive[idx] = true; this.regrowAt[idx] = 0; this.remaining[idx] = ASSETS.defs[id].hits; this.container[idx] = null;
    const key = (Math.floor(x / A_CELL) + A_OFF) * A_STRIDE + (Math.floor(z / A_CELL) + A_OFF);
    let b = this.grid.get(key); if (!b) { b = []; this.grid.set(key, b); } b.push(idx);
    return true;
  }

  // Deterministic jittered-grid scatter across the whole island (even coverage incl. the spawn coast), shuffled
  // so truncation to the cap stays uniform. Each habitable cell gets at most one prop, weighted by ASSETS.defs.
  private fill(): void {
    this.rng = mulberry32(ASSET_SEED);
    this.grid.clear(); this.count = 0; this.aid.length = 0; this.alive.length = 0; this.regrowAt.length = 0; this.remaining.length = 0; this.container.length = 0;
    const R = TERRAIN.islandOuter, spacing = ASSETS.spacing;
    const cand: Array<[number, number, AssetId]> = [];
    for (let gz = -R; gz <= R; gz += spacing) {
      for (let gx = -R; gx <= R; gx += spacing) {
        const cx = gx + (this.rng() - 0.5) * spacing * 0.85;
        const cz = gz + (this.rng() - 0.5) * spacing * 0.85;
        if (cx * cx + cz * cz > R * R) continue;
        const id = this.kindAt(cx, cz);
        if (id) cand.push([cx, cz, id]);
      }
    }
    for (let i = cand.length - 1; i > 0; i--) { const j = (this.rng() * (i + 1)) | 0; const t = cand[i]; cand[i] = cand[j]; cand[j] = t; }
    for (let i = 0; i < cand.length && this.count < this.MAX; i++) {
      const [cx, cz, id] = cand[i];
      this.place(cx, cz, id);
      // Some sites cluster extra props around the centre (barrels + a crate, etc.). Junk piles stay solo singletons.
      if (!id.startsWith('junk') && this.rng() < SITE_MULTI_CHANCE) {
        const cy = terrainHeight(cx, cz);
        const b = biomeAt(cx, cz, cy);
        const wild = b === Biome.Forest || b === Biome.Hills || b === Biome.Rocky;
        const extra = 1 + ((this.rng() * 3) | 0); // 1..3 extra members
        for (let k = 0; k < extra && this.count < this.MAX; k++) {
          const ang = this.rng() * Math.PI * 2, rr = SITE_R * (0.45 + this.rng() * 0.55);
          const mx = cx + Math.cos(ang) * rr, mz = cz + Math.sin(ang) * rr;
          if (terrainHeight(mx, mz) <= TERRAIN.seaLevel + 0.5) continue; // keep members out of the water
          this.place(mx, mz, this.siteMember(wild)); // each member seats ITSELF (centre-height clamping buried them on slopes)
        }
      }
    }
    for (const id of SALVAGE_IDS) { this.meshes[id].count = 0; this.visible[id].length = 0; }
    this.streamCellX = NaN; this.streamCellZ = NaN;
  }

  // A salvage prop spawns on dry land (not water/steep). ~60% of land cells get one (the rest stay empty so it
  // reads as scattered loot, not a carpet). The archetype is a weighted roll, lightly biased: military/junk loot
  // favours the wilder inland biomes (forest/rocky), barrels/crates appear everywhere.
  private kindAt(x: number, z: number): AssetId | null {
    const h = terrainHeight(x, z);
    if (h <= TERRAIN.seaLevel + 1.5) return null;          // not in the water
    if (this.rng() > 0.6) return null;                     // sparse: most land cells stay empty
    const b = biomeAt(x, z, h);
    const wild = b === Biome.Forest || b === Biome.Hills || b === Biome.Rocky;
    let r = this.rng() * this.totalWeight;
    for (const id of SALVAGE_IDS) {
      let w = ASSETS.defs[id].weight;
      // bias the higher-tier loot (military crate, junk5) toward wilder ground; thin it on open/coast cells
      if ((id === 'crateMilitary' || id === 'junk5') && !wild) w *= 0.4;
      r -= w;
      if (r <= 0) return id;
    }
    return 'barrelGrey';
  }

  // Roll an extra cluster-member archetype (barrels/crates/toolbox only -- junk never clusters), biome-biased like
  // kindAt (military crate favours wilder ground).
  private siteMember(wild: boolean): AssetId {
    const w = (id: AssetId): number => { let v = ASSETS.defs[id].weight; if (id === 'crateMilitary' && !wild) v *= 0.4; return v; };
    const total = SITE_PROPS.reduce((s, id) => s + w(id), 0);
    let r = this.rng() * total;
    for (const id of SITE_PROPS) { r -= w(id); if (r <= 0) return id; }
    return 'barrelGrey';
  }

  // TEST: drop one of each salvageable kind in a ring near (px,pz) so they can all be tried at spawn.
  seedNear(px: number, pz: number): void {
    if (this.disabled) return;
    SALVAGE_IDS.forEach((id, i) => {
      const a = (i / SALVAGE_IDS.length) * Math.PI * 2, r = 6 + (i % 2) * 3;
      const x = px + Math.cos(a) * r, z = pz + Math.sin(a) * r;
      if (terrainHeight(x, z) > TERRAIN.seaLevel) this.place(x, z, id);
    });
    this.streamCellX = NaN;
  }

  // Nearest alive asset of a given salvage method within range, or -1 (grid-accelerated).
  private nearestOf(px: number, pz: number, range: number, method: 'open' | 'destroy'): number {
    if (this.disabled) return -1;
    const r2 = range * range; let best = -1, bestD = r2;
    const cx0 = Math.floor((px - range) / A_CELL), cx1 = Math.floor((px + range) / A_CELL);
    const cz0 = Math.floor((pz - range) / A_CELL), cz1 = Math.floor((pz + range) / A_CELL);
    for (let cx = cx0; cx <= cx1; cx++) for (let cz = cz0; cz <= cz1; cz++) {
      const bucket = this.grid.get((cx + A_OFF) * A_STRIDE + (cz + A_OFF)); if (!bucket) continue;
      for (const i of bucket) {
        if (!this.alive[i] || ASSETS.defs[this.aid[i]].method !== method) continue;
        const dx = this.ax[i] - px, dz = this.az[i] - pz, dd = dx * dx + dz * dz;
        if (dd < bestD) { bestD = dd; best = i; }
      }
    }
    return best;
  }
  nearestOpen(px: number, pz: number, range: number): number { return this.nearestOf(px, pz, range, 'open'); }
  nearestDestroy(px: number, pz: number, range: number): number { return this.nearestOf(px, pz, range, 'destroy'); }

  // The prop the player is AIMING at (cursor in iso, crosshair in FP) -> absolute slot index, or -1. Resolves the
  // hit InstancedMesh + instanceId through the LIVE visible[] map every call (never cached -- streamRebuild repacks
  // it on every camera-cell move and every salvage/respawn). Nearest hit first, so in a cluster you target the prop
  // your ray meets first (barrel vs crate). Range-gate at the call site.
  raycastSlot(raycaster: THREE.Raycaster): number {
    if (this.disabled || this.count === 0) return -1;
    for (const h of raycaster.intersectObjects(this.meshList, false)) {
      const id = h.object.userData.assetId as AssetId | undefined;
      if (!id || h.instanceId == null) continue;
      const slot = this.visible[id][h.instanceId];
      if (slot != null && this.alive[slot]) return slot;
    }
    return -1;
  }

  idAt(i: number): AssetId | null { return i >= 0 && i < this.count ? this.aid[i] : null; }
  posAt(i: number): { x: number; z: number } | null { return i >= 0 && i < this.count ? { x: this.ax[i], z: this.az[i] } : null; }

  // Fill `out` with alive DESTROY-method props near (px,pz) as bullet spheres (slot + centre + radius) so the gun
  // hit-test can break barrels/junk. Grid-walked like nearestOf; resolved LIVE each frame (slots are stable, but
  // alive/positions are read fresh). Crates (open) are deliberately NOT bullet targets.
  // Add an oriented AABB per alive prop near (px,pz) to the world collider set so the player + bullets stop on
  // barrels/crates (mirrors rocks/nodes collectColliders). Grid-walked; reads the GRID so a prop is solid even
  // when its mesh is culled out of the draw stream.
  collectColliders(out: Box[], pool: BoxPool, px: number, pz: number, range: number): void {
    if (this.disabled) return;
    const r2 = range * range;
    const cx0 = Math.floor((px - range) / A_CELL), cx1 = Math.floor((px + range) / A_CELL);
    const cz0 = Math.floor((pz - range) / A_CELL), cz1 = Math.floor((pz + range) / A_CELL);
    for (let cx = cx0; cx <= cx1; cx++) for (let cz = cz0; cz <= cz1; cz++) {
      const bucket = this.grid.get((cx + A_OFF) * A_STRIDE + (cz + A_OFF)); if (!bucket) continue;
      for (const i of bucket) {
        if (!this.alive[i]) continue;
        const dx = this.ax[i] - px, dz = this.az[i] - pz; if (dx * dx + dz * dz > r2) continue;
        const e = ASSET_EXTENT[this.aid[i]] ?? AE_BARREL, s = this.ascale[i];
        out.push(pool.take(this.ax[i], this.ay[i] + e.cy * s, this.az[i], e.hx * s, e.hy * s, e.hz * s, this.ayaw[i]));
      }
    }
  }

  // PRECISE pick: the slot whose collider box the aim ray enters FIRST (nearest along the ray), or -1. Independent
  // grid walk (not tied to the streamed visible[] or the shared worldBoxes), so it's reliable per-prop in a cluster
  // -- this is what makes mouse-targeting one specific barrel work. Range-gate the result by centre distance at the call site.
  pickSlot(raycaster: THREE.Raycaster, px: number, pz: number, range: number): number {
    if (this.disabled || this.count === 0) return -1;
    const o = raycaster.ray.origin, d = raycaster.ray.direction;
    _P0.copy(o); _P1.copy(o).addScaledVector(d, range);
    let best = -1, bestT = Infinity;
    const cx0 = Math.floor((px - range) / A_CELL), cx1 = Math.floor((px + range) / A_CELL);
    const cz0 = Math.floor((pz - range) / A_CELL), cz1 = Math.floor((pz + range) / A_CELL);
    for (let cx = cx0; cx <= cx1; cx++) for (let cz = cz0; cz <= cz1; cz++) {
      const bucket = this.grid.get((cx + A_OFF) * A_STRIDE + (cz + A_OFF)); if (!bucket) continue;
      for (const i of bucket) {
        if (!this.alive[i]) continue;
        const e = ASSET_EXTENT[this.aid[i]] ?? AE_BARREL, s = this.ascale[i];
        _BOX.cx = this.ax[i]; _BOX.cy = this.ay[i] + e.cy * s; _BOX.cz = this.az[i];
        _BOX.hx = e.hx * s; _BOX.hy = e.hy * s; _BOX.hz = e.hz * s; _BOX.yaw = this.ayaw[i];
        const t = segmentBoxT(_P0, _P1, _BOX);
        if (t >= 0 && t < bestT) { bestT = t; best = i; }
      }
    }
    return best;
  }

  collectBulletTargets(out: Array<{ slot: number; x: number; y: number; z: number; r: number }>, px: number, pz: number, range: number): void {
    out.length = 0;
    if (this.disabled) return;
    const cx0 = Math.floor((px - range) / A_CELL), cx1 = Math.floor((px + range) / A_CELL);
    const cz0 = Math.floor((pz - range) / A_CELL), cz1 = Math.floor((pz + range) / A_CELL);
    const r2 = range * range;
    for (let cx = cx0; cx <= cx1; cx++) for (let cz = cz0; cz <= cz1; cz++) {
      const bucket = this.grid.get((cx + A_OFF) * A_STRIDE + (cz + A_OFF)); if (!bucket) continue;
      for (const i of bucket) {
        if (!this.alive[i] || ASSETS.defs[this.aid[i]].method !== 'destroy') continue;
        const dx = this.ax[i] - px, dz = this.az[i] - pz; if (dx * dx + dz * dz > r2) continue;
        out.push({ slot: i, x: this.ax[i], y: this.ay[i] + 0.7 * this.ascale[i], z: this.az[i], r: 0.95 * this.ascale[i] });
      }
    }
  }

  // Fired ONCE when a 'destroy' asset (barrel / junk pile) is fully smashed, with its world XZ. MP wires this to a
  // persisted despawn op (crateAt) so the smashed prop stays gone on relog + for late joiners -- barrels used to
  // replay as intact because only crate-EMPTY was persisted. Fires on the local smash AND on a remote replay; the
  // MP layer guards the remote case so it does not re-broadcast.
  onDestroyed: (x: number, z: number) => void = () => {};

  // Apply one salvage action to a slot. 'open' assets break in one go; 'destroy' assets take `hits` swings.
  // Returns the archetype id + whether it broke this call (the authority rolls + grants the loot on break).
  salvage(i: number, hits = 1): { id: AssetId; depleted: boolean } | null {
    if (i < 0 || i >= this.count || !this.alive[i]) return null;
    const id = this.aid[i];
    this.remaining[i] -= hits; // < 1 from a torch (slow smasher); 1 from a real tool / a bullet
    if (this.remaining[i] > 0) return { id, depleted: false };
    this.alive[i] = false;
    this.regrowAt[i] = this.nowSec + assetRespawnSec(this.ax[i], this.az[i]);
    this.streamCellX = NaN; // force a re-stream so it disappears promptly
    this.onDestroyed(this.ax[i], this.az[i]); // persist the destroy so it does not come back on relog
    return { id, depleted: true };
  }

  // Cosmetic hit-wobble on a prop that took a non-breaking hit. Ignored if dead or currently posed open.
  jolt(slot: number): void {
    if (slot < 0 || slot >= this.count || !this.alive[slot]) return;
    if (this.anim.get(slot)?.kind === 'open') return;
    this.anim.set(slot, { kind: 'jolt', t0: this.nowSec });
  }

  // Recompose one slot's instance matrix with an optional tilt (about the base) + vertical lift. instId is the LIVE
  // index in visible[id]; callers resolve it via visible[id].indexOf(slot) (never cached -- streamRebuild repacks).
  private writeSlotMatrix(slot: number, instId: number, tilt: number, lift: number): void {
    this.tmpPos.set(this.ax[slot], this.ay[slot] + lift, this.az[slot]);
    this.tmpQuat.setFromAxisAngle(UP, this.ayaw[slot]);
    if (tilt !== 0) { _TILT_Q.setFromAxisAngle(_TILT_AXIS, tilt); this.tmpQuat.premultiply(_TILT_Q); }
    this.tmpScale.setScalar(this.ascale[slot]);
    this.tmp.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
    this.meshes[this.aid[slot]].setMatrixAt(instId, this.tmp);
  }

  // Per-frame: perturb the instance matrices of animating slots (jolt decays + expires; open lean is held). Runs at
  // the END of update() so streamRebuild has refreshed visible[]/clean matrices first; we layer the pose on top.
  private animTick(): void {
    if (this.anim.size === 0) return;
    const touched = new Set<AssetId>();
    for (const [slot, st] of this.anim) {
      if (!this.alive[slot]) { this.anim.delete(slot); continue; }
      const id = this.aid[slot];
      const instId = this.visible[id].indexOf(slot);
      if (instId < 0) continue; // streamed out: keep the state, animate when it streams back in
      if (st.kind === 'open') { this.writeSlotMatrix(slot, instId, OPEN_TILT, 0.04 * this.ascale[slot]); touched.add(id); continue; }
      const e = this.nowSec - st.t0;
      if (e >= JOLT_DUR) { this.writeSlotMatrix(slot, instId, 0, 0); this.anim.delete(slot); touched.add(id); continue; } // settle clean
      const env = 1 - e / JOLT_DUR;
      this.writeSlotMatrix(slot, instId, 0.12 * env * Math.sin(e * 55), 0.02 * env * Math.abs(Math.sin(e * 70)) * this.ascale[slot]);
      touched.add(id);
    }
    for (const id of touched) this.meshes[id].instanceMatrix.needsUpdate = true;
  }

  // OPEN a crate/toolbox as a chest-style loot container: roll its loot into an Inventory on FIRST open, then
  // return that same inventory on every re-open. The crate stays in the world (re-openable) until it's emptied.
  openContainer(i: number): Inventory | null {
    if (i < 0 || i >= this.count || !this.alive[i] || ASSETS.defs[this.aid[i]].method !== 'open') return null;
    if (!this.container[i]) {
      const inv = new Inventory(12); // crates open as a 6x2 grid
      // DETERMINISTIC roll (seeded off the crate's position) so every client sees the SAME loot in this crate.
      for (const e of rollAssetLoot(this.aid[i], assetSeed(this.ax[i], this.az[i]))) inv.add(e.id, e.amount);
      this.container[i] = inv;
    }
    this.anim.set(i, { kind: 'open', t0: this.nowSec }); // lean it open while being looted
    return this.container[i];
  }

  // Called when a crate's loot UI closes: clear the open lean; if the player emptied it, despawn + start
  // respawn. Returns true if it was emptied (so the caller can broadcast a crateAt op -> despawn it for all).
  closeContainerIfEmpty(i: number): boolean {
    if (i < 0 || i >= this.count) return false;
    this.anim.delete(i);
    const inv = this.container[i];
    if (inv && inv.isEmpty()) {
      this.alive[i] = false;
      this.regrowAt[i] = this.nowSec + assetRespawnSec(this.ax[i], this.az[i]);
      this.container[i] = null;
      this.streamCellX = NaN; // re-stream so the emptied crate disappears
      return true;
    } else if (this.alive[i]) {
      const instId = this.visible[this.aid[i]].indexOf(i); // crate stays -> snap the lean back to flat now
      if (instId >= 0) { this.writeSlotMatrix(i, instId, 0, 0); this.meshes[this.aid[i]].instanceMatrix.needsUpdate = true; }
    }
    return false;
  }

  // MP: a crate someone else emptied (crateAt op) -> despawn the matching open-crate slot near (x,z) and
  // start its respawn, exactly as if we had emptied it ourselves. Nearest openable within `r`, or a no-op.
  despawnAt(x: number, z: number, r: number): void {
    let best = -1, bestD = r * r;
    for (let i = 0; i < this.count; i++) {
      if (!this.alive[i] || ASSETS.defs[this.aid[i]].method !== 'open') continue;
      const dx = this.ax[i] - x, dz = this.az[i] - z, dd = dx * dx + dz * dz;
      if (dd < bestD) { bestD = dd; best = i; }
    }
    if (best < 0) return;
    this.alive[best] = false;
    this.regrowAt[best] = this.nowSec + assetRespawnSec(this.ax[best], this.az[best]);
    this.container[best] = null;
    this.streamCellX = NaN; // re-stream so the emptied crate disappears here too
  }

  // Mark the prop at (x,z) SALVAGED (gone until it respawns), for ANY method -- a smashed 'destroy' barrel/junk OR an
  // emptied 'open' crate. Used by the MP salvaged-set replay (welcome + live crateAt op) so a prop someone smashed
  // stays gone for late joiners + across relog. TIGHT radius (the op carries the prop's exact position) so a dense
  // composite site never despawns a neighbour. No-op if nothing's there / already gone.
  markSalvagedAt(x: number, z: number, remainingSec?: number): void {
    let best = -1, bestD = 0.36; // (0.6m)^2 -- far tighter than the inter-prop spacing, so it hits the exact prop
    for (let i = 0; i < this.count; i++) {
      if (!this.alive[i]) continue;
      const dx = this.ax[i] - x, dz = this.az[i] - z, dd = dx * dx + dz * dz;
      if (dd < bestD) { bestD = dd; best = i; }
    }
    if (best < 0) return;
    this.alive[best] = false;
    // A server-supplied remaining time (welcome replay) makes the respawn land at the SAME moment on every client;
    // a live op without it (someone smashing it right now) just uses the local window -- both clients are ~in sync then.
    this.regrowAt[best] = this.nowSec + (remainingSec !== undefined ? Math.max(1, remainingSec) : assetRespawnSec(this.ax[best], this.az[best]));
    this.remaining[best] = 0;
    this.container[best] = null;
    this.streamCellX = NaN; // re-stream so it disappears here too
  }

  // World save: only the props that differ from the pristine layout (salvaged / dented / part-looted).
  serialize(): SavedSalvage[] {
    const out: SavedSalvage[] = [];
    if (this.disabled) return out;
    for (let i = 0; i < this.count; i++) {
      if (!this.alive[i]) { out.push({ k: aKey(this.ax[i], this.az[i]), rem: Math.max(1, this.regrowAt[i] - this.nowSec) }); continue; }
      const dented = this.remaining[i] < ASSETS.defs[this.aid[i]].hits;
      const cont = this.container[i];
      if (dented || cont) out.push({ k: aKey(this.ax[i], this.az[i]), hits: dented ? this.remaining[i] : undefined, cont: cont ? cont.serializeSlots() : undefined });
    }
    return out;
  }

  // Re-apply salvaged/dented state on boot (nowSec still ~0, so `rem` lands directly on the fresh
  // clock). Unmatched keys drop silently: a relaid prop field just means those spots respawn fresh.
  deserialize(data: SavedSalvage[]): void {
    if (this.disabled || data.length === 0) return;
    const byKey = new Map<string, SavedSalvage>();
    for (const e of data) byKey.set(e.k, e);
    for (let i = 0; i < this.count && byKey.size > 0; i++) {
      const key = aKey(this.ax[i], this.az[i]);
      const e = byKey.get(key);
      if (!e) continue;
      byKey.delete(key);
      if (e.rem !== undefined && e.rem > 0) {
        this.alive[i] = false;
        this.regrowAt[i] = this.nowSec + e.rem;
        this.container[i] = null;
        continue;
      }
      if (e.hits !== undefined) this.remaining[i] = Math.min(e.hits, ASSETS.defs[this.aid[i]].hits);
      if (e.cont) { const inv = new Inventory(12); inv.deserializeSlots(e.cont); this.container[i] = inv; }
    }
    this.streamCellX = NaN; // re-stream so restored gaps show from frame one
  }

  // Per-frame: advance the respawn clock (revive ripe slots) + stream nearby props.
  update(camX: number, camZ: number, nowSec: number): void {
    if (this.disabled) return;
    this.nowSec = nowSec;
    let revived = false;
    for (let i = 0; i < this.count; i++) if (!this.alive[i] && this.regrowAt[i] > 0 && nowSec >= this.regrowAt[i]) { this.alive[i] = true; this.regrowAt[i] = 0; this.remaining[i] = ASSETS.defs[this.aid[i]].hits; this.container[i] = null; revived = true; }
    const cellX = Math.floor(camX / NODES.streamCell), cellZ = Math.floor(camZ / NODES.streamCell);
    if (revived || cellX !== this.streamCellX || cellZ !== this.streamCellZ) { this.streamCellX = cellX; this.streamCellZ = cellZ; this.streamRebuild(camX, camZ); }
    this.animTick(); // layer jolt/open poses on top of the (now current) instance matrices
  }

  private streamRebuild(camX: number, camZ: number): void {
    const R = ASSETS.drawRadius, r2 = R * R;
    for (const id of SALVAGE_IDS) this.visible[id].length = 0;
    const cx0 = Math.floor((camX - R) / A_CELL), cx1 = Math.floor((camX + R) / A_CELL);
    const cz0 = Math.floor((camZ - R) / A_CELL), cz1 = Math.floor((camZ + R) / A_CELL);
    for (let cx = cx0; cx <= cx1; cx++) for (let cz = cz0; cz <= cz1; cz++) {
      const bucket = this.grid.get((cx + A_OFF) * A_STRIDE + (cz + A_OFF)); if (!bucket) continue;
      for (const i of bucket) {
        if (!this.alive[i]) continue;
        const dx = this.ax[i] - camX, dz = this.az[i] - camZ; if (dx * dx + dz * dz > r2) continue;
        const id = this.aid[i], vis = this.visible[id], slot = vis.length; if (slot >= this.meshes[id].instanceMatrix.count) continue;
        this.tmpPos.set(this.ax[i], this.ay[i], this.az[i]); this.tmpQuat.setFromAxisAngle(UP, this.ayaw[i]); this.tmpScale.setScalar(this.ascale[i]);
        this.tmp.compose(this.tmpPos, this.tmpQuat, this.tmpScale); this.meshes[id].setMatrixAt(slot, this.tmp); vis.push(i);
      }
    }
    for (const id of SALVAGE_IDS) { const m = this.meshes[id]; m.count = this.visible[id].length; m.instanceMatrix.needsUpdate = true; m.computeBoundingSphere(); }
  }
}
